import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success("Welcome to TRAM");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 tram-grain">
      <div
        className="hidden lg:flex relative text-white p-14 flex-col justify-between overflow-hidden"
        style={{ backgroundColor: "#0f1c3f", color: "#ffffff" }}
      >
        <div className="absolute inset-0 opacity-10 select-none pointer-events-none">
          <div
            className="absolute -right-10 -bottom-20 font-serif-display font-bold"
            style={{ fontSize: 380, letterSpacing: "0.08em", lineHeight: 0.85 }}
          >
            TR
          </div>
        </div>
        <div className="relative">
          <div className="w-11 h-11 rounded-md bg-white/10 border border-white/20 flex items-center justify-center font-serif-display text-xl font-bold">
            TR
          </div>
          <div className="mt-3 overline text-white/60">TRAM · Recruitment Agency · Est 2024</div>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-serif-display text-5xl leading-[1.05] font-semibold">
            Standardising recruitment excellence.
          </h1>
          <p className="mt-5 text-[15px] text-white/70 leading-relaxed">
            Upload any candidate CV and let TRAM automatically reformat it into our signature template
            — polished, editable, and ready to send.
          </p>
        </div>
        <div className="relative overline text-white/50">
          Deep navy · Cream ivory · Cormorant Garamond
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white border border-[#e2ddd3] rounded-xl p-10 shadow-[0_4px_28px_rgba(15,28,63,0.06)]"
          data-testid="auth-form"
        >
          <div className="overline mb-2">
            {mode === "login" ? "Sign In" : "Create Account"}
          </div>
          <h2 className="font-serif-display text-3xl text-[#0f1c3f] font-semibold">
            {mode === "login" ? "Welcome back" : "Join TRAM"}
          </h2>
          <p className="text-sm text-[#5a6578] mt-1">
            {mode === "login"
              ? "Use your team credentials to continue."
              : "Register a workspace account."}
          </p>

          <div className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="overline">Name</Label>
                <Input
                  data-testid="input-name"
                  className="mt-1 border-[#cfc8ba]"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}
            <div>
              <Label className="overline">Email</Label>
              <Input
                data-testid="input-email"
                type="email"
                className="mt-1 border-[#cfc8ba]"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label className="overline">Password</Label>
              <Input
                data-testid="input-password"
                type="password"
                className="mt-1 border-[#cfc8ba]"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="submit-auth"
            disabled={loading}
            className="mt-8 w-full bg-[#0f1c3f] hover:bg-[#0a122a] text-white h-11 rounded-md"
          >
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>

          <div className="mt-6 text-center text-sm text-[#5a6578]">
            {mode === "login" ? "New here? " : "Have an account? "}
            <button
              type="button"
              data-testid="switch-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#0f1c3f] font-semibold underline underline-offset-4"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
